/* eslint-disable @typescript-eslint/ban-types */
import { defaultMetadataStorage } from 'class-transformer/cjs/storage';
import { getFromContainer, getMetadataStorage, IsOptional, MetadataStorage, } from 'class-validator';
import { API_MODEL_PROPERTIES, API_MODEL_PROPERTIES_ARRAY, defineApiPropertyMetadata, } from '../../decorators/api-property-metadata.decorator';
export function applyIsOptionalDecorator(targetClass, propertyKey) {
    const decoratorFactory = IsOptional();
    decoratorFactory(targetClass.prototype, propertyKey);
}
export function inheritValidationMetadata(parentClass, targetClass, isPropertyInherited) {
    try {
        const metadataStorage = getMetadataStorage
            ? getMetadataStorage()
            : getFromContainer(MetadataStorage);
        const getTargetValidationMetadatasArgs = [parentClass, null, false, false];
        const targetMetadata = metadataStorage.getTargetValidationMetadatas(...getTargetValidationMetadatasArgs);
        return targetMetadata
            .filter(({ propertyName }) => !isPropertyInherited || isPropertyInherited(propertyName))
            .map((value) => {
            const originalType = Reflect.getMetadata('design:type', parentClass.prototype, value.propertyName);
            if (originalType) {
                Reflect.defineMetadata('design:type', originalType, targetClass.prototype, value.propertyName);
            }
            metadataStorage.addValidationMetadata(Object.assign(Object.assign({}, value), { target: targetClass }));
            return value.propertyName;
        });
    }
    catch (err) {
        console.error(`Validation ("class-validator") metadata cannot be inherited for "${parentClass.name}" class.`);
        console.error(err);
    }
}
export function inheritTransformationMetadata(parentClass, targetClass, isPropertyInherited) {
    try {
        const transformMetadataKeys = [
            '_excludeMetadatas',
            '_exposeMetadatas',
            '_transformMetadatas',
            '_typeMetadatas',
        ];
        transformMetadataKeys.forEach((key) => inheritTransformerMetadata(key, parentClass, targetClass, isPropertyInherited));
    }
    catch (err) {
        console.error(`Transformer ("class-transformer") metadata cannot be inherited for "${parentClass.name}" class.`);
        console.error(err);
    }
}
function inheritTransformerMetadata(key, parentClass, targetClass, isPropertyInherited) {
    // const classTransformer = require('class-transformer/cjs/storage');
    const metadataStorage /*: typeof import('class-transformer/types/storage').defaultMetadataStorage */ = defaultMetadataStorage;
    while (parentClass && parentClass !== Object) {
        if (metadataStorage[key].has(parentClass)) {
            const metadataMap = metadataStorage[key];
            const parentMetadata = metadataMap.get(parentClass);
            const targetMetadataEntries = Array.from(parentMetadata.entries())
                .filter(([key]) => !isPropertyInherited || isPropertyInherited(key))
                .map(([key, metadata]) => {
                if (Array.isArray(metadata)) {
                    // "_transformMetadatas" is an array of elements
                    const targetMetadata = metadata.map((item) => (Object.assign(Object.assign({}, item), { target: targetClass })));
                    return [key, targetMetadata];
                }
                return [key, Object.assign(Object.assign({}, metadata), { target: targetClass })];
            });
            if (metadataMap.has(targetClass)) {
                const existingRules = metadataMap.get(targetClass).entries();
                metadataMap.set(targetClass, new Map([...existingRules, ...targetMetadataEntries]));
            }
            else {
                metadataMap.set(targetClass, new Map(targetMetadataEntries));
            }
        }
        parentClass = Object.getPrototypeOf(parentClass);
    }
}
// Added to each class by the @nestjs/swagger CLI plugin. @nestjs/swagger itself is not imported, because this
// package also runs in browsers.
const OPENAPI_METADATA_FACTORY = '_OPENAPI_METADATA_FACTORY';
/**
 * Copies the Swagger property metadata of `parentClass` to `targetClass`, as @nestjs/swagger's own mapped
 * types do: the `_OPENAPI_METADATA_FACTORY` the CLI plugin adds to each class, and explicit `@ApiProperty()`
 * metadata. `optional` marks the copied properties as not required (PartialType).
 * Does nothing when the parent has neither, as in the browser build, which is compiled without the plugin.
 */
export function inheritSwaggerMetadata(parentClass, targetClass, isPropertyInherited, optional = false) {
    var _a;
    const inherit = (metadata) => {
        const inherited = {};
        Object.keys(metadata)
            .filter((key) => !isPropertyInherited || isPropertyInherited(key))
            .forEach((key) => {
            inherited[key] = optional
                ? Object.assign(Object.assign({}, metadata[key]), { required: false }) : metadata[key];
        });
        return inherited;
    };
    // Plugin factories of the parent and its ancestors, nearest first. They are called only when the document
    // is built, as the plugin intends.
    const factoryOwners = [];
    for (let proto = parentClass.prototype; proto && proto !== Object.prototype; proto = Object.getPrototypeOf(proto)) {
        const owner = proto.constructor;
        if (owner &&
            Object.prototype.hasOwnProperty.call(owner, OPENAPI_METADATA_FACTORY)) {
            factoryOwners.push(owner);
        }
    }
    if (factoryOwners.length) {
        const target = targetClass;
        const targetFactory = target[OPENAPI_METADATA_FACTORY];
        target[OPENAPI_METADATA_FACTORY] = () => (Object.assign(Object.assign({}, targetFactory === null || targetFactory === void 0 ? void 0 : targetFactory()), inherit(factoryOwners.reduceRight((metadata, owner) => (Object.assign(Object.assign({}, metadata), owner[OPENAPI_METADATA_FACTORY]())), {}))));
    }
    if (typeof Reflect !== 'object' ||
        typeof Reflect.getMetadata !== 'function') {
        return;
    }
    const decorated = {};
    ((_a = Reflect.getMetadata(API_MODEL_PROPERTIES_ARRAY, parentClass.prototype)) !== null && _a !== void 0 ? _a : []).forEach((entry) => {
        var _a;
        if (typeof entry !== 'string' || !entry.startsWith(':'))
            return;
        const key = entry.slice(1);
        decorated[key] =
            (_a = Reflect.getMetadata(API_MODEL_PROPERTIES, parentClass.prototype, key)) !== null && _a !== void 0 ? _a : {};
    });
    Object.entries(inherit(decorated)).forEach(([key, metadata]) => defineApiPropertyMetadata(targetClass.prototype, key, metadata));
}
export function inheritPropertyInitializers(target, sourceClass, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
isPropertyInherited = (key) => true) {
    try {
        const tempInstance = new sourceClass();
        const propertyNames = Object.getOwnPropertyNames(tempInstance);
        propertyNames
            .filter((propertyName) => typeof tempInstance[propertyName] !== 'undefined' &&
            typeof target[propertyName] === 'undefined')
            .filter((propertyName) => isPropertyInherited(propertyName))
            .forEach((propertyName) => {
            target[propertyName] = tempInstance[propertyName];
        });
    }
    catch (_a) { }
}
